import { FC, useCallback, useContext, useMemo, useState } from "react";
import Timeline from "@mui/lab/Timeline";
import {
  TabContext,
  TabList,
  TabPanel,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  timelineItemClasses,
  TimelineSeparator,
} from "@mui/lab";
import {
  Box,
  capitalize,
  Colors,
  Drawer,
  List,
  ListItem,
  Tab,
} from "@analyticsMD/quil";
import History from "@mui/icons-material/History";
import UpdateHistoryEntry from "../types/UpdateHistoryEntry";
import { Sources } from "../types/Sources";
import { css } from "@emotion/css";
import moment from "moment";
import APIContext from "../contexts/APIContext";
import { TypeChoices } from "../types/TypeChoices";
import EDD from "../types/EDD";
import PatientDisposition from "../types/PatientDisposition";
import { DATE_TIME_FORMAT } from "../utils/utils";
import { deepPurple } from "@mui/material/colors";
import DrawerHeader from "./drawer-header";

const sxStyles = {
  icon: {
    marginRight: "0.25rem",
    color: `${deepPurple[600]} !important`,
    cursor: "pointer",
  },
  close: { alignSelf: "flex-end", cursor: "pointer" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    margin: "1.5rem",
  },
  drawerPanel: {
    minWidth: "28rem",
    overflowY: "none",
  },
  listItem: { paddingTop: "0.125rem", paddingBottom: "0.125rem" },
  clickableContainer: {
    display: "flex",
    alignItems: "center",
    color: deepPurple[600],
    fontWeight: "500",
    fontSize: "0.775rem",
    cursor: "pointer",
  },
};

const cssStyles = {
  label: css`
    color: ${Colors.grey_30};
    padding-right: 0.25rem;
  `,
  defaultText: css`
    color: ${Colors.grey_40};
    font-size: 0.85rem;
    margin-left: 0.25rem;
  `,
};

const humanizedSources = {
  [Sources.AUTO_POPULATED]: "Qventus Autopopulated",
  [Sources.EHR_GENERATED]: "EHR",
  [Sources.QVENTUS_SUGGESTED]: "Qventus Accepted Suggestion",
  [Sources.USER_CREATED]: "QCard",
};

const UpdateHistory: FC = () => {
  const { eddQuery, patientDispositionsQuery } = useContext(APIContext);

  const defaultText = "(default)";

  const eddUpdateHistory = useMemo(() => {
    const userEDD =
      eddQuery.isSuccess &&
      eddQuery.data?.find((edd) => edd.edd_type === TypeChoices.USER_ENTERED);

    const eddUpdates =
      userEDD && userEDD.history
        ? userEDD.history.filter((edd: EDD) => {
            const isUpdateInLookback = moment(edd.qv_updated_ts).isSameOrAfter(
              moment().subtract("1", "weeks")
            );

            return isUpdateInLookback;
          })
        : [];

    // always return the current value if there are no updates in the last week
    if (eddUpdates.length === 0 && userEDD) {
      const formattedEDD = moment(userEDD.edd_value).format("MM/DD/YY");

      return [
        {
          id: userEDD.id,
          value: `${formattedEDD}, ${userEDD.is_am_discharge ? "AM" : "PM"}`,
          update_type: userEDD.edd_type,
          timestamp:
            userEDD.edd_type === TypeChoices.AUTO_POPULATED
              ? userEDD.decision_updated_ts
              : userEDD.qv_updated_ts,
          updated_by: userEDD.updated_by_name
            ? capitalize(userEDD.updated_by_name.toLowerCase())
            : userEDD.updated_by
            ? userEDD.updated_by
            : "Qventus",
          source: userEDD.source,
          requires_default: !userEDD.is_am_discharge,
        },
      ];
    }

    return eddUpdates.map((edd: EDD) => {
      const formattedEDD = moment(edd.edd_value).format("MM/DD/YY");
      return {
        id: edd.id,
        value: `${formattedEDD}, ${edd.is_am_discharge ? "AM" : "PM"}`,
        update_type: edd.edd_type,
        timestamp:
          edd.edd_type === TypeChoices.AUTO_POPULATED
            ? edd.decision_updated_ts
            : edd.qv_updated_ts,
        updated_by: edd.updated_by_name
          ? capitalize(edd.updated_by_name.toLowerCase())
          : edd.updated_by
          ? edd.updated_by
          : "Qventus",
        source: edd.source,
        requires_default: !edd.is_am_discharge,
      };
    });
  }, [eddQuery.data, eddQuery.isSuccess]);

  const dispositionUpdateHistory = useMemo(() => {
    const userDisposition =
      patientDispositionsQuery.isSuccess &&
      patientDispositionsQuery.data?.find(
        (disposition) =>
          disposition.disposition_type === TypeChoices.USER_ENTERED
      );

    const dispositionUpdates =
      userDisposition && userDisposition.history
        ? userDisposition.history.filter((disposition: PatientDisposition) => {
            // we only care about the last week of dispositions
            const isUpdateInLookback = moment(
              disposition.qv_updated_ts
            ).isSameOrAfter(moment().subtract("1", "weeks"));

            return isUpdateInLookback;
          })
        : [];

    // always return the current value if there are no updates in the last week
    if (dispositionUpdates.length === 0 && userDisposition) {
      return [
        {
          id: userDisposition.id,
          value:
            typeof userDisposition.disposition === "number"
              ? userDisposition.disposition
              : userDisposition.disposition.display_name,
          update_type: userDisposition.disposition_type,
          timestamp:
            userDisposition.disposition_type === TypeChoices.AUTO_POPULATED
              ? userDisposition.decision_updated_ts
              : userDisposition.qv_updated_ts,
          updated_by: userDisposition.updated_by_name
            ? capitalize(userDisposition.updated_by_name.toLowerCase())
            : userDisposition.updated_by
            ? userDisposition.updated_by
            : "Qventus",
          source: userDisposition.source,
        },
      ];
    }

    return dispositionUpdates.map((disposition: PatientDisposition) => ({
      id: disposition.id,
      value:
        typeof disposition.disposition === "number"
          ? disposition.disposition
          : disposition.disposition.display_name,
      update_type: disposition.disposition_type,
      timestamp:
        disposition.disposition_type === TypeChoices.AUTO_POPULATED
          ? disposition.decision_updated_ts
          : disposition.qv_updated_ts,
      updated_by: disposition.updated_by_name
        ? capitalize(disposition.updated_by_name.toLowerCase())
        : disposition.updated_by
        ? disposition.updated_by
        : "Qventus",
      source: disposition.source,
    }));
  }, [patientDispositionsQuery.data, patientDispositionsQuery.isSuccess]);

  const generateTimelineContent = useCallback((update: UpdateHistoryEntry) => {
    const formattedTimestamp = update.timestamp
      ? moment(update.timestamp).format(DATE_TIME_FORMAT)
      : "";

    return (
      <Box>
        <List dense disablePadding sx={{ fontSize: "0.9rem" }}>
          <ListItem sx={sxStyles.listItem}>
            <span className={cssStyles.label}>Value:</span>
            {update.value}
            {update.requires_default && (
              <span className={cssStyles.defaultText}>{defaultText}</span>
            )}
          </ListItem>
          <ListItem sx={sxStyles.listItem}>
            <span className={cssStyles.label}>Updated by:</span>
            {update.updated_by}
          </ListItem>
          <ListItem sx={sxStyles.listItem}>
            <span className={cssStyles.label}>Source:</span>
            {humanizedSources[update.source]}
          </ListItem>
          <ListItem sx={sxStyles.listItem}>
            <span className={cssStyles.label}>Update time:</span>
            {formattedTimestamp}
          </ListItem>
        </List>
      </Box>
    );
  }, []);

  const [showHistory, setShowHistory] = useState(false);

  const updates = {
    EDD: eddUpdateHistory,
    Disposition: dispositionUpdateHistory,
  };

  const updateTypes = Object.keys(updates);

  const [tabValue, setTabValue] = useState(updateTypes[0]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const resetTabs = () => {
    setShowHistory(false);
    setTabValue(updateTypes[0]);
  };

  const handleHistoryClick = () => {
    setShowHistory(!showHistory);
  };

  return (
    <>
      <Box sx={sxStyles.clickableContainer} onClick={handleHistoryClick}>
        <History sx={sxStyles.icon} />
        <span>View History</span>
      </Box>
      <Drawer
        anchor="right"
        open={showHistory}
        onClose={() => resetTabs()}
        ModalProps={{
          BackdropProps: { sx: { background: "none" } },
        }}
        PaperProps={{
          sx: sxStyles.drawerPanel,
        }}
      >
        <DrawerHeader title="Recent History" onClose={() => resetTabs()} />
        <TabContext value={tabValue}>
          <Box sx={{ borderBottom: 1, borderColor: Colors.grey_15 }}>
            <TabList
              TabIndicatorProps={{
                sx: { backgroundColor: deepPurple[600] },
              }}
              onChange={handleTabChange}
            >
              {updateTypes.map((key) => (
                <Tab
                  label={key}
                  key={key}
                  value={key}
                  sx={{ textTransform: "capitalize" }}
                />
              ))}
            </TabList>
          </Box>
          <Timeline
            sx={{
              overflowY: "auto",
              [`& .${timelineItemClasses.root}:before`]: {
                flex: 0,
                padding: 0,
              },
            }}
          >
            {Object.entries(updates).map(([entryType, entries]) => {
              const generateEntryTimeline = () =>
                entries
                  .sort((a, b) =>
                    moment(b.timestamp).diff(moment(a.timestamp), "seconds")
                  )
                  .map((entry, idx) => (
                    <TimelineItem key={entry.id}>
                      <TimelineSeparator>
                        <TimelineDot
                          sx={{
                            backgroundColor: deepPurple[400],
                            boxShadow: "none",
                          }}
                        />
                        {idx < entries.length - 1 && <TimelineConnector />}
                      </TimelineSeparator>
                      <TimelineContent sx={{ paddingTop: ".5rem" }}>
                        {generateTimelineContent(entry)}
                      </TimelineContent>
                    </TimelineItem>
                  ));

              return (
                <TabPanel key={entryType} value={entryType}>
                  {generateEntryTimeline()}
                </TabPanel>
              );
            })}
          </Timeline>
        </TabContext>
      </Drawer>
    </>
  );
};

export default UpdateHistory;
